import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import { OffersService, Offer } from '../../services/offersService';

interface MyOffersPopupProps {
  open: boolean;
  onClose: () => void;
  highlightOfferId?: string | null;
  onOfferDeleted?: (offerId: string) => void;
}

const MyOffersPopup: React.FC<MyOffersPopupProps> = ({ open, onClose, highlightOfferId, onOfferDeleted }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [offers, setOffers] = React.useState<Offer[]>([]);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!open) return;
      setLoading(true);
      setError(null);
      try {
        const data = await OffersService.getMyOffers();
        if (!cancelled) setOffers(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load your offers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  const handleDelete = async (offerId: string) => {
    const confirmed = window.confirm('Delete this offer? This cannot be undone.');
    if (!confirmed) return;
    setError(null);
    setDeletingId(offerId);
    try {
      await OffersService.deleteOffer(offerId);
      setOffers(prev => prev.filter(o => o.id !== offerId));
      onOfferDeleted?.(offerId);
    } catch (e: any) {
      setError(e?.message || 'Failed to delete offer');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight="bold">Your Offers</Typography>
          {highlightOfferId && (
            <Typography variant="caption" sx={{ bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1, fontFamily: 'monospace' }}>
              Selected: {highlightOfferId.substring(0, 8)}...
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading your offers...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!loading && !error && offers.length === 0 && (
          <Alert severity="info">You have not made any offers yet.</Alert>
        )}

        {!loading && !error && offers.length > 0 && (
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {offers.map((offer) => {
              const isHighlighted = highlightOfferId && offer.id === highlightOfferId;
              return (
                <React.Fragment key={offer.id}>
                  <ListItem
                    sx={{
                      alignItems: 'flex-start',
                      bgcolor: isHighlighted ? 'primary.50' : 'transparent',
                      borderLeft: isHighlighted ? '4px solid' : 'none',
                      borderLeftColor: isHighlighted ? 'primary.main' : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            ${offer.offer_price?.toFixed ? offer.offer_price.toFixed(2) : offer.offer_price}
                          </Typography>
                          <Chip size="small" label={(offer.status || 'pending').toUpperCase()} />
                          <Typography variant="caption" sx={{ ml: 1, fontFamily: 'monospace' }}>
                            ID: {offer.id.substring(0, 8)}...
                          </Typography>
                          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              disabled={deletingId === offer.id}
                              onClick={() => handleDelete(offer.id)}
                              sx={{ textTransform: 'none' }}
                            >
                              {deletingId === offer.id ? 'Deleting…' : 'Delete'}
                            </Button>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                            {offer.offer_message || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(offer.created_at as any).toLocaleString()}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 'bold' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MyOffersPopup;


